import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Shield,
  CheckCircle,
  AlertCircle,
  User,
  Eye,
  EyeOff,
  Mail,
  Lock,
  Phone,
  MapPin,
  Map
} from 'lucide-react';
import { useTalentAuth } from '../../contexts/TalentAuthContext';
import './TalentRegister.css';
import Header from '../public/header';
import Footer from '../public/Footer';

import ServiceLocationMap from './ServiceLocationMap';

declare global {
  interface Window {
    google: any;
  }
}

// Type for geocoder results
type GeocodeResult = {
  formatted_address: string;
  // Add other properties as needed
};

type GeocodeStatus = 'OK' | 'ZERO_RESULTS' | 'OVER_QUERY_LIMIT' | 'REQUEST_DENIED' | 'INVALID_REQUEST' | 'UNKNOWN_ERROR';





const TalentRegister: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    location: '',
    latitude: 0,
    longitude: 0,
    city: '',
    parish: '',
    bio: '',
    acceptTerms: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showMap, setShowMap] = useState(false); // Can be used to toggle map visibility if needed
  const locationInputRef = useRef<HTMLInputElement>(null);

  const { isAuthenticated, register, loginWithGoogle } = useTalentAuth();
  const navigate = useNavigate();

  // Handle Google OAuth callback
  const handleGoogleCallback = useCallback(async (response: any) => {
    try {
      setIsGoogleLoading(true);
      setMessage({ type: '', text: '' });

      const result = await loginWithGoogle(response.credential);

      if (result.success) {
        setMessage({ type: 'success', text: 'Google sign-up successful! Redirecting to dashboard...' });
        // TEMPORARY BYPASS: Redirect to dashboard instead of membership selection
        setTimeout(() => {
          navigate('/talent/dashboard');
        }, 2000);
      } else {
        setMessage({ type: 'error', text: result.message || 'Google sign-up failed. Please try again.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Google sign-up failed. Please try again.' });
    } finally {
      setIsGoogleLoading(false);
    }
  }, [loginWithGoogle, navigate]);

  // Initialize Google OAuth
  useEffect(() => {
    const initializeGoogleOAuth = () => {
      if (window.google && window.google.accounts) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || '877295110750-se0qnm049lgsn63pga699j5npljnc6oq.apps.googleusercontent.com',
          callback: handleGoogleCallback,
        });
      }
    };

    // Load Google OAuth script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = initializeGoogleOAuth;
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [handleGoogleCallback]);

  // Handle Google sign-in button click
  const handleGoogleSignIn = () => {
    if (window.google && window.google.accounts) {
      window.google.accounts.id.prompt();
    } else {
      setMessage({ type: 'error', text: 'Google Sign-In is not available. Please try again later.' });
    }
  };


  // Initialize Google Places Autocomplete for location field
  useEffect(() => {
    let autocomplete: google.maps.places.Autocomplete | null = null;
    let intervalId: NodeJS.Timeout | null = null;

    const initAutocomplete = () => {
      if (locationInputRef.current && window.google?.maps?.places) {
        const autocompleteInstance = new window.google.maps.places.Autocomplete(locationInputRef.current, {
          types: ['geocode', 'establishment'],
        });
        autocomplete = autocompleteInstance;

        autocompleteInstance.addListener('place_changed', () => {
          const place = autocompleteInstance.getPlace();
          if (place && place.formatted_address) {
            const address = place.formatted_address;
            
            // Extract geometry if available
            let lat = 0;
            let lng = 0;
            let city = '';
            let parish = '';

            if (place.geometry && place.geometry.location) {
              lat = place.geometry.location.lat();
              lng = place.geometry.location.lng();
            }

            if (place.address_components) {
              for (const component of place.address_components) {
                if (component.types.includes('locality')) {
                  city = component.long_name;
                }
                const parishList = [
                  'Kingston', 'St. Andrew', 'St. Thomas', 'Portland', 'St. Mary',
                  'St. Ann', 'Trelawny', 'St. James', 'Hanover', 'Westmoreland',
                  'St. Elizabeth', 'Manchester', 'Clarendon', 'St. Catherine'
                ];
                const match = parishList.find(p => component.long_name.includes(p) || p.includes(component.long_name));
                if (match) {
                  parish = match;
                }
              }
            }

            setFormData(prev => ({
              ...prev,
              location: address,
              latitude: lat || prev.latitude,
              longitude: lng || prev.longitude,
              city: city || prev.city,
              parish: parish || prev.parish
            }));

            if (errors.location) {
              setErrors(prev => ({ ...prev, location: '' }));
            }
          }
        });

        if (intervalId) clearInterval(intervalId);
      }
    };

    // Try immediately
    initAutocomplete();

    // Or poll until API is loaded
    intervalId = setInterval(initAutocomplete, 1000);

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (autocomplete) {
        window.google.maps.event.clearInstanceListeners(autocomplete);
      }
    };
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/talent/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear field error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    if (!formData.bio.trim()) {
      newErrors.bio = 'Please tell us about yourself';
    }

    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        address: formData.location,
        latitude: formData.latitude,
        longitude: formData.longitude,
        city: formData.city,
        parish: formData.parish,
        bio: formData.bio,
        skills: [], // Initialize with empty array
        availability: 'Full Time', // Default value
        hourlyRate: 0, // Default value
        experienceYears: 0, // Default value
        acceptTerms: formData.acceptTerms
      });

      if (result.success) {
        setMessage({
          type: 'success',
          text: 'Registration successful! Redirecting to dashboard...'
        });

        // TEMPORARY BYPASS: Redirect to dashboard instead of membership selection
        setTimeout(() => {
          navigate('/talent/dashboard');
        }, 2000);
      } else {
        setMessage({
          type: 'error',
          text: result.message || 'Registration failed. Please try again.'
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'An unexpected error occurred. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className='partner-login new-talent-css'>
        <div className='partner-login-box'>
          <h3>Join as Service Provider</h3>
          <p>Create your professional profile and connect with clients</p>

          {message.text && (
            <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Name Fields */}
            <div className="form-row" style={{ display: 'flex', gap: '15px' }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label htmlFor="firstName" className="form-label">First Name</label>
                <div className="input-wrapper">
                  <div className="input-icon">
                    <User size={18} />
                  </div>
                  <input
                    type="text"
                    className={`form-control ${errors.firstName ? 'error' : ''}`}
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="Enter your first name"
                  />
                </div>
                {errors.firstName && <div className="error-message">{errors.firstName}</div>}
              </div>

              <div className="form-group" style={{ flex: 1 }}>
                <label htmlFor="lastName" className="form-label">Last Name</label>
                <div className="input-wrapper">
                  <div className="input-icon">
                    <User size={18} />
                  </div>
                  <input
                    type="text"
                    className={`form-control ${errors.lastName ? 'error' : ''}`}
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Enter your last name"
                  />
                </div>
                {errors.lastName && <div className="error-message">{errors.lastName}</div>}
              </div>
            </div>

            {/* Email */}
            <div className="form-group">
              <label htmlFor="email" className="form-label">Email Address</label>
              <div className="input-wrapper">
                <div className="input-icon">
                  <Mail size={18} />
                </div>
                <input
                  type="email"
                  className={`form-control ${errors.email ? 'error' : ''}`}
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                />
              </div>
              {errors.email && <div className="error-message">{errors.email}</div>}
            </div>

            {/* Phone */}
            <div className="form-group">
              <label htmlFor="phone" className="form-label">Phone Number</label>
              <div className="input-wrapper">
                <div className="input-icon">
                  <Phone size={18} />
                </div>
                <input
                  type="tel"
                  className={`form-control ${errors.phone ? 'error' : ''}`}
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter your phone number"
                />
              </div>
              {errors.phone && <div className="error-message">{errors.phone}</div>}
            </div>

            {/* Password Fields */}
            <div className="form-row" style={{ display: 'flex', gap: '15px' }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label htmlFor="password" className="form-label">Password</label>
                <div className="input-wrapper">
                  <div className="input-icon">
                    <Lock size={18} />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className={`form-control ${errors.password ? 'error' : ''}`}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.password && <div className="error-message">{errors.password}</div>}
              </div>

              <div className="form-group" style={{ flex: 1 }}>
                <label htmlFor="confirmPassword" className="form-label">Confirm Password</label>
                <div className="input-wrapper">
                  <div className="input-icon">
                    <Lock size={18} />
                  </div>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    className={`form-control ${errors.confirmPassword ? 'error' : ''}`}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.confirmPassword && <div className="error-message">{errors.confirmPassword}</div>}
              </div>
            </div>

            {/* Location */}
            <div className="form-group">
              <label htmlFor="location" className="form-label">Location</label>
              <div className="input-wrapper">
                <div className="input-icon">
                  <MapPin size={18} />
                </div>
                <input
                  ref={locationInputRef}
                  type="text"
                  className={`form-control ${errors.location ? 'error' : ''}`}
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="Enter your location or select on map below"
                />
              </div>
              {errors.location && <div className="error-message">{errors.location}</div>}

              {/* Map Controls */}
              <div style={{ marginTop: '15px' }}>
                <ServiceLocationMap
                  isEditing={true}
                  onLocationChange={(lat, lng, address, city, parish) => {
                    setFormData(prev => ({
                      ...prev,
                      location: address,
                      latitude: lat,
                      longitude: lng,
                      city: city || '',
                      parish: parish || ''
                    }));
                    // Clear location error if it exists
                    if (errors.location) {
                      setErrors(prev => ({ ...prev, location: '' }));
                    }
                  }}
                  initialAddress={formData.location}
                  initialCoordinates={
                    formData.latitude && formData.longitude
                      ? { lat: formData.latitude, lng: formData.longitude }
                      : undefined
                  }
                />
              </div>
            </div>

            {/* Bio */}
            <div className="form-group">
              <label htmlFor="bio" className="form-label">About</label>
              <textarea
                className={`form-control ${errors.bio ? 'error' : ''}`}
                id="bio"
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                placeholder="Tell us about yourself, your experience, and what makes you unique..."
                rows={4}
                style={{
                  padding: '12px 15px',
                  border: errors.bio ? '2px solid #e74c3c' : '2px solid #e9ecef',
                  borderRadius: '10px',
                  fontSize: '14px',
                  transition: 'all 0.3s ease',
                  background: '#f8f9fa',
                  resize: 'vertical',
                  minHeight: '80px'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3498db';
                  e.target.style.background = 'white';
                  e.target.style.boxShadow = '0 0 0 3px rgba(52, 152, 219, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = errors.bio ? '#e74c3c' : '#e9ecef';
                  e.target.style.background = '#f8f9fa';
                  e.target.style.boxShadow = 'none';
                }}
              />
              {errors.bio && <div className="error-message">{errors.bio}</div>}
            </div>

            {/* Terms Checkbox */}
            <div className="form-group">
              <label className="remember-me">
                <input
                  type="checkbox"
                  name="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={handleInputChange}
                />
                <span>I agree to the <Link to="/terms" className="link-placeholder">Terms of Service</Link> and <Link to="/privacy" className="link-placeholder">Privacy Policy</Link></span>
              </label>
              {errors.acceptTerms && <div className="error-message">{errors.acceptTerms}</div>}
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="button-spinner"></div>
                  <span>Creating Account...</span>
                </>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          <div className="or-divider">
            <span>or</span>
          </div>

          <div className='continue-google'>
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={isGoogleLoading || loading}
              className={`google-signin-btn ${isGoogleLoading ? 'loading' : ''}`}
            >
              {isGoogleLoading ? (
                <>
                  <div className="button-spinner"></div>
                  <span>Signing up with Google...</span>
                </>
              ) : (
                <>
                  <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="LgbsSe-Bz112c">
                    <g>
                      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                      <path fill="none" d="M0 0h48v48H0z"></path>
                    </g>
                  </svg>
                  Continue with Google
                </>
              )}
            </button>
            <p className="google-note">If you sign up with Google, you'll need to complete your profile after registration.</p>
          </div>

          <div className="login-footer">
            <p>Already have an account? <Link to="/login">Sign in here</Link></p>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default TalentRegister;